/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import "./App.scss";

import { BrowserAuthorizationClient } from "@itwin/browser-authorization";
import type { ScreenViewport, MapLayerOptions } from "@itwin/core-frontend";
import { DisplayStyleSettingsProps } from "@itwin/core-common";
import { FitViewTool, IModelApp, StandardViewId } from "@itwin/core-frontend";
import { FillCentered } from "@itwin/core-react";
import { ProgressLinear } from "@itwin/itwinui-react";
import {
    useAccessToken,
    Viewer,
    ViewerPerformance,
} from "@itwin/web-viewer-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Header } from "./Header";
import { history } from "./history";

import { Visualization } from "./Visualization";
import { RealityData } from "./RealityData";

import { InfrastructureIotConfigService, InfrastructureIotItemsProvider, InfrastructureIotToolAdmin } from "@itwin/infrastructure-iot-react";

const App: React.FC = () => {

    // Set up map layer options with our Bing Maps API key
    const mapLayerOptions: MapLayerOptions = {
        BingMaps: {
            key: "key",
            value: "AmG6MOaGbRWF4wXUIFw6Mr7H3gwkqW_pmMj2xM1CzMf3S1Is-Q78WOTeOVD3eW2V"
        }
    };

    const [iModelId, setIModelId] = useState(process.env.IMJS_IMODEL_ID);
    const [iTwinId, setITwinId] = useState(process.env.IMJS_ITWIN_ID);
    const [iotEnvironment, setIotEnvironment] = useState(process.env.IOT_ENVIRONMENT);

    const accessToken = useAccessToken();

    const authClient = useMemo(
        () =>
            new BrowserAuthorizationClient({
                scope: process.env.IMJS_AUTH_CLIENT_SCOPES ?? "",
                clientId: process.env.IMJS_AUTH_CLIENT_CLIENT_ID ?? "",
                redirectUri: process.env.IMJS_AUTH_CLIENT_REDIRECT_URI ?? "",
                postSignoutRedirectUri: process.env.IMJS_AUTH_CLIENT_LOGOUT_URI,
                responseType: "code",
                authority: process.env.IMJS_AUTH_AUTHORITY,
            }),
        []
    );

    const login = useCallback(async () => {
        try {
            await authClient.signInSilent();
        } catch {
            await authClient.signIn();
        }
    }, [authClient]);

    useEffect(() => {
        void login();
    }, [login]);

    useEffect(() => {
        if (accessToken) {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has("iTwinId")) {
                setITwinId(urlParams.get("iTwinId") as string);
            } else {
                if (!process.env.IMJS_ITWIN_ID) {
                    throw new Error(
                        "Please add a valid iTwin ID in the .env file and restart the application or add it to the iTwinId query parameter in the url and refresh the page. See the README for more information."
                    );
                }
            }

            if (urlParams.has("iModelId")) {
                setIModelId(urlParams.get("iModelId") as string);
            } else {
                if (!process.env.IMJS_IMODEL_ID) {
                    throw new Error(
                        "Please add a valid iModel ID in the .env file and restart the application or add it to the iModelId query parameter in the url and refresh the page. See the README for more information."
                    );
                }
            }

            if (urlParams.has("environment")) {
                setIotEnvironment(urlParams.get("environment") as string);
            }
        }
    }, [accessToken]);

    useEffect(() => {
        if (accessToken && iTwinId && iModelId) {
            history.push(`?iTwinId=${iTwinId}&iModelId=${iModelId}&environment=${iotEnvironment || "development"}`);
        }
    }, [accessToken, iTwinId, iModelId, iotEnvironment]);

    const onLoginClick = async () => {
        await authClient.signIn();
    };

    const onLogoutClick = async () => {
        await authClient.signOut();
    };

    const onIModelConnected = () => {
        IModelApp.viewManager.onViewOpen.addOnce(async (viewport: ScreenViewport) => {

            // This is done as a performance optimization for the viewer as a whole
            const viewStyle: DisplayStyleSettingsProps = {
                viewflags: {
                    // visEdges: false,
                    // shadows: false
                }
            };
            viewport.overrideDisplayStyle(viewStyle);

            // Hide house exterior (works for house model only)
            Visualization.hideHouseExterior(viewport);

            // Load any reality data in this iModel
            const availRealityModels = await RealityData.getRealityModels(viewport.iModel);
            for (const model of availRealityModels) {
                RealityData.toggleRealityModel(model, viewport, true);
                RealityData.setRealityDataTransparency(model, viewport, 0);
            }

            // Initialize infrastructure-iot package config service
            await InfrastructureIotConfigService.initialize({
                environment: iotEnvironment as any || "development",
                enableLogging: true
            });
        });
    };

    /** NOTE: This function will execute the "Fit View" tool after the iModel is loaded into the Viewer.
     * This will provide an "optimal" view of the model. However, it will override any default views that are
     * stored in the iModel. Delete this function and the prop that it is passed to if you prefer
     * to honor default views when they are present instead (the Viewer will still apply a similar function to iModels that do not have a default view).
     */
    const viewConfiguration = useCallback((viewPort: ScreenViewport) => {
        // default execute the fitview tool and use the iso standard view after tile trees are loaded
        const tileTreesLoaded = () => {
            return new Promise((resolve, reject) => {
                const start = new Date();
                const intvl = setInterval(() => {
                    if (viewPort.areAllTileTreesLoaded) {
                        ViewerPerformance.addMark("TilesLoaded");
                        void ViewerPerformance.addAndLogMeasure(
                            "TileTreesLoaded",
                            "ViewerStarting",
                            "TilesLoaded",
                            viewPort.numReadyTiles
                        );
                        clearInterval(intvl);
                        resolve(true);
                    }
                    const now = new Date();
                    // after 20 seconds, stop waiting and fit the view
                    if (now.getTime() - start.getTime() > 20000) {
                        reject();
                    }
                }, 100);
            });
        };

        tileTreesLoaded().finally(() => {
            void IModelApp.tools.run(FitViewTool.toolId, viewPort, true, false);
            viewPort.view.setStandardRotation(StandardViewId.Iso);
        });
    }, []);

    const viewCreatorOptions = useMemo(
        () => ({ viewportConfigurer: viewConfiguration }),
        [viewConfiguration]
    );

    return (
        <div className="viewer-container">
            <Header
                handleLogin={onLoginClick}
                loggedIn={!!accessToken}
                handleLogout={onLogoutClick}
            />
            {!accessToken && (
                <FillCentered>
                    <div className="signin-content">
                        <ProgressLinear indeterminate={true} labels={["Signing in..."]} />
                    </div>
                </FillCentered>
            )}
            <Viewer
                iTwinId={iTwinId}
                iModelId={iModelId}
                authClient={authClient}
                mapLayerOptions={mapLayerOptions}
                onIModelConnected={onIModelConnected}
                uiProviders={[new InfrastructureIotItemsProvider()]}
                toolAdmin={InfrastructureIotToolAdmin}
                viewCreatorOptions={viewCreatorOptions}
                enablePerformanceMonitors={true} // see description in the README (https://www.npmjs.com/package/@itwin/desktop-viewer-react)
            />
        </div>
    );
};

export default App;
